import { supabase } from './supabase';
import { broadcastCreditChange } from './credit-events';

export interface AdCreativeCreditBalance {
  userId: string;
  email: string;
  planName: string;
  monthlyAllocation: number;
  purchasedCredits: number;
  usedThisPeriod: number;
  totalAvailable: number;
  periodStart: string;
  periodEnd: string;
}

export interface AdCreativeCreditPackage {
  id: string;
  name: string;
  credits: number;
  priceCents: number;
  priceUsd: number;
  discountPercentage: number;
  stripePriceId: string | null;
  isActive: boolean;
  description: string;
  expiryDays: number;
}

export interface AdCreativeCreditCheck {
  canPerform: boolean;
  availableCredits: number;
  message: string;
}

export class AdCreativeCreditService {
  /**
   * Check if user can perform an ad-creative action (team-aware)
   */
  static async canPerformAdCreativeAction(): Promise<AdCreativeCreditCheck> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return {
          canPerform: false,
          availableCredits: 0,
          message: 'User not authenticated'
        };
      }

      console.log('🎨 Checking ad-creative credits (team-aware) for user:', user.id);

      // Always use team-aware RPC function - it handles both team members and individuals
      const { data, error } = await supabase
        .rpc('can_user_perform_ad_creative_action_team_aware', {
          user_uuid: user.id
        });

      if (error) {
        console.error('Error checking ad-creative credits:', error);
        return {
          canPerform: false,
          availableCredits: 0,
          message: 'Error checking credits'
        };
      }

      const result = data?.[0];

      console.log('🎨 Ad-creative credit check result:', {
        canPerform: result?.can_perform || false,
        availableCredits: result?.available_credits || 0,
        message: result?.message || 'Unable to check credits'
      });

      return {
        canPerform: result?.can_perform || false,
        availableCredits: result?.available_credits || 0,
        message: result?.message || 'Unable to check credits'
      };
    } catch (error) {
      console.error('Error in canPerformAdCreativeAction:', error);
      return {
        canPerform: false,
        availableCredits: 0,
        message: 'Error checking credits'
      };
    }
  }

  /**
   * Deduct ad-creative credits for an action (team-aware)
   */
  static async deductAdCreativeCredits(
    creditsToDeduct: number = 1,
    reason: string = 'Ad Creative Action'
  ): Promise<{ success: boolean; remainingCredits: number; transactionId?: string }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      console.log('🎨 Deducting ad creative credits (team-aware):', {
        userId: user.id,
        creditsToDeduct,
        reason
      });

      const { data, error } = await supabase
        .rpc('deduct_ad_creative_credits_team_aware', {
          user_uuid: user.id,
          credits_to_deduct: creditsToDeduct,
          action_reason: reason
        });

      if (error) {
        console.error('Error deducting ad-creative credits:', error);
        return { success: false, remainingCredits: 0 };
      }

      const result = data?.[0];
      if (result?.success) {
        // Broadcast credit change event
        broadcastCreditChange(
          'credit_used',
          user.id,
          -creditsToDeduct,
          result.remaining_credits,
          { reason, creditType: 'ad_creative' }
        );
      }

      return {
        success: result?.success || false,
        remainingCredits: result?.remaining_credits || 0,
        transactionId: result?.transaction_id
      };
    } catch (error) {
      console.error('Error in deductAdCreativeCredits:', error);
      return { success: false, remainingCredits: 0 };
    }
  }

  /**
   * Get current ad-creative credit balance (team-aware)
   */
  static async getAdCreativeCreditBalance(): Promise<AdCreativeCreditBalance | null> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Check if user is part of an organization
      const { data: userData, error: userDataError } = await supabase
        .from('users')
        .select('organization_id, team_role, subscription_plan, email')
        .eq('id', user.id)
        .single();

      if (userDataError) {
        console.error('Error fetching user data for ad creative balance:', userDataError);
        return null;
      }

      // If user is a team member, get organization credits via the database function
      if (userData.organization_id && userData.team_role) {
        // Check if this user is a team member (not organization owner)
        const { data: orgData } = await supabase
          .from('organizations')
          .select('created_by')
          .eq('id', userData.organization_id)
          .single();
        
        if (orgData?.created_by && orgData.created_by !== user.id) {
          // This is a team member - use the admin function to bypass RLS for organization owner
          console.log('🔄 Team member detected - getting ad creative credits for organization owner:', {
            teamMember: user.id,
            organizationOwner: orgData.created_by
          });
          
          const { data: balanceData, error: balanceError } = await supabase
            .rpc('get_team_aware_ad_creative_balance', {
              target_user_id: orgData.created_by // Use organization owner's ID
            });

          if (balanceError || !balanceData?.[0]) {
            console.error('Error getting team ad creative balance:', balanceError);
            return null;
          }

          const balance = balanceData[0];

          // Return team member balance with organization owner's data
          return {
            userId: user.id, // Keep team member's ID for display
            email: userData.email, // Keep team member's email
            planName: `${balance.plan_name} (Organization)`, // Show it's from organization
            monthlyAllocation: balance.monthly_allocation,
            purchasedCredits: Number(balance.purchased_credits),
            usedThisPeriod: Number(balance.used_this_period),
            totalAvailable: Number(balance.total_available),
            periodStart: balance.period_start,
            periodEnd: balance.period_end
          };
        }
      }

      // Individual user - use existing logic
      const { data, error } = await supabase
        .from('ad_creative_credit_balance')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching ad-creative credit balance:', error);
        return null;
      }

      if (!data) {
        // User doesn't have a credit balance record yet - check if they're on trial plan
        const { data: user_data } = await supabase
          .from('users')
          .select('subscription_plan, email')
          .eq('id', user.id)
          .single();
          
        if (user_data?.subscription_plan === 'trial') {
          // Return trial plan ad creative credits (1 credit)
          return {
            userId: user.id,
            email: user_data.email,
            planName: 'Free Trial',
            monthlyAllocation: 1,
            purchasedCredits: 0,
            usedThisPeriod: 0,
            totalAvailable: 1,
            periodStart: new Date().toISOString(),
            periodEnd: new Date().toISOString()
          };
        }
        
        return null;
      }

      return {
        userId: data.user_id,
        email: data.email,
        planName: data.plan_name,
        monthlyAllocation: data.monthly_allocation,
        purchasedCredits: data.purchased_credits,
        usedThisPeriod: data.used_this_period,
        totalAvailable: data.total_available,
        periodStart: data.period_start,
        periodEnd: data.period_end
      };
    } catch (error) {
      console.error('Error in getAdCreativeCreditBalance:', error);
      return null;
    }
  }

  /**
   * Get available ad-creative credit packages
   */
  static async getAvailablePackages(): Promise<AdCreativeCreditPackage[]> {
    try {
      const { data, error } = await supabase
        .from('ad_creative_credit_packages')
        .select('*')
        .eq('is_active', true)
        .order('price_cents', { ascending: true });

      if (error) {
        console.error('Error fetching ad-creative packages:', error);
        return [];
      }

      return data.map(pkg => ({
        id: pkg.id,
        name: pkg.name,
        credits: pkg.credits,
        priceCents: pkg.price_cents,
        priceUsd: pkg.price_usd,
        discountPercentage: pkg.discount_percentage || 0,
        stripePriceId: pkg.stripe_price_id,
        isActive: pkg.is_active,
        description: pkg.description || '',
        expiryDays: pkg.expiry_days || 90
      }));
    } catch (error) {
      console.error('Error in getAvailablePackages:', error);
      return [];
    }
  }

  /**
   * Get ad-creative credit transaction history
   */
  static async getTransactionHistory(limit: number = 50): Promise<any[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', user.id)
        .eq('credit_type', 'ad_creative')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching transaction history:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getTransactionHistory:', error);
      return [];
    }
  }

  /**
   * Get credit usage breakdown
   */
  static async getCreditUsageBreakdown(): Promise<{
    enhancement: number;
    generation: number;
    regeneration: number;
    total: number;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('credit_transactions')
        .select('reason, amount')
        .eq('user_id', user.id)
        .eq('credit_type', 'ad_creative')
        .eq('transaction_type', 'deduction')
        .gte('created_at', new Date(new Date().setDate(1)).toISOString());

      if (error) {
        console.error('Error fetching usage breakdown:', error);
        return { enhancement: 0, generation: 0, regeneration: 0, total: 0 };
      }

      const breakdown = {
        enhancement: 0,
        generation: 0,
        regeneration: 0,
        total: 0
      };

      data?.forEach(transaction => {
        const amount = Math.abs(transaction.amount);
        breakdown.total += amount;

        if (transaction.reason.includes('Enhancement')) {
          breakdown.enhancement += amount;
        } else if (transaction.reason.includes('Regeneration')) {
          breakdown.regeneration += amount;
        } else if (transaction.reason.includes('Generation')) {
          breakdown.generation += amount;
        }
      });

      return breakdown;
    } catch (error) {
      console.error('Error in getCreditUsageBreakdown:', error);
      return { enhancement: 0, generation: 0, regeneration: 0, total: 0 };
    }
  }

  /**
   * Check if credits are running low (less than 10%)
   */
  static async isRunningLow(): Promise<{ isLow: boolean; percentage: number }> {
    try {
      const balance = await this.getAdCreativeCreditBalance();
      if (!balance) {
        return { isLow: true, percentage: 0 };
      }

      const totalMonthly = balance.monthlyAllocation;
      const percentage = totalMonthly > 0 
        ? (balance.totalAvailable / totalMonthly) * 100 
        : 0;

      return {
        isLow: percentage < 10,
        percentage: Math.round(percentage)
      };
    } catch (error) {
      console.error('Error checking if credits are low:', error);
      return { isLow: true, percentage: 0 };
    }
  }

  /**
   * Get expiring credits (within 7 days)
   */
  static async getExpiringCredits(): Promise<{
    amount: number;
    expiresIn: string;
  } | null> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

      const { data, error } = await supabase
        .from('credit_transactions')
        .select('amount, expires_at')
        .eq('user_id', user.id)
        .eq('credit_type', 'ad_creative')
        .in('transaction_type', ['purchase', 'referral_bonus'])
        .not('expires_at', 'is', null)
        .lte('expires_at', sevenDaysFromNow.toISOString())
        .gt('expires_at', new Date().toISOString())
        .gt('amount', 0);

      if (error || !data || data.length === 0) {
        return null;
      }

      const totalExpiring = data.reduce((sum, t) => sum + t.amount, 0);
      const earliestExpiry = data.reduce((earliest, t) => 
        !earliest || new Date(t.expires_at) < new Date(earliest) ? t.expires_at : earliest, 
        null as string | null
      );

      if (!earliestExpiry || totalExpiring === 0) {
        return null;
      }

      const daysUntilExpiry = Math.ceil(
        (new Date(earliestExpiry).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
      );

      return {
        amount: totalExpiring,
        expiresIn: `${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`
      };
    } catch (error) {
      console.error('Error getting expiring credits:', error);
      return null;
    }
  }

  /**
   * Purchase ad creative credit package using Stripe checkout
   */
  static async purchasePackage(
    packageId: string,
    options?: {
      successUrl?: string;
      cancelUrl?: string;
    }
  ): Promise<{
    success: boolean;
    checkoutUrl?: string;
    error?: string;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return {
          success: false,
          error: 'User not authenticated'
        };
      }

      // Get the ad creative credit package
      const { data: packageData, error: packageError } = await supabase
        .from('ad_creative_credit_packages')
        .select('*')
        .eq('id', packageId)
        .eq('is_active', true)
        .single();

      if (packageError || !packageData) {
        return {
          success: false,
          error: 'Ad creative credit package not found'
        };
      }

      if (!packageData.stripe_price_id) {
        return {
          success: false,
          error: 'Package not available for purchase'
        };
      }

      // Get user email for Stripe checkout
      const { data: userData, error: userDataError } = await supabase
        .from('users')
        .select('email')
        .eq('id', user.id)
        .single();

      if (userDataError) {
        return {
          success: false,
          error: 'Failed to get user information'
        };
      }

      // Create Stripe checkout session
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('stripe-checkout-v2', {
        body: {
          action: 'create_checkout_session',
          params: {
            success_url: options?.successUrl || `${window.location.origin}/dashboard/credits?purchase_success=true&session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: options?.cancelUrl || `${window.location.origin}/dashboard/credits?purchase_cancelled=true`,
            mode: 'payment',
            line_items: [
              {
                price: packageData.stripe_price_id,
                quantity: 1,
              },
            ],
            metadata: {
              user_id: user.id,
              package_id: packageData.id,
              package_name: packageData.name,
              credits: packageData.credits.toString(),
              type: 'credit_purchase',
              credit_type: 'ad_creative'
            },
            customer_email: userData.email
          }
        }
      });

      if (checkoutError) {
        console.error('Stripe checkout error:', checkoutError);
        return {
          success: false,
          error: `Failed to create checkout session: ${checkoutError.message}`
        };
      }

      if (!checkoutData || !checkoutData.url) {
        return {
          success: false,
          error: 'Invalid response from payment processor'
        };
      }

      return {
        success: true,
        checkoutUrl: checkoutData.url
      };
    } catch (error) {
      console.error('Error purchasing ad creative credits:', error);
      return {
        success: false,
        error: 'Failed to process purchase request'
      };
    }
  }

  /**
   * Verify checkout session status (webhook-only processing for ad creative credits)
   */
  static async verifyAndProcessPurchase(sessionId: string): Promise<{
    success: boolean;
    paymentCompleted: boolean;
    creditsPurchased: number;
    error?: string;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return {
          success: false,
          paymentCompleted: false,
          creditsPurchased: 0,
          error: 'User not authenticated'
        };
      }

      // Verify session status using stripe-checkout-v2
      const { data: sessionData, error: sessionError } = await supabase.functions.invoke('stripe-checkout-v2', {
        body: {
          action: 'verify_session_status',
          params: {
            session_id: sessionId
          }
        }
      });

      if (sessionError) {
        console.error('Session verification error:', sessionError);
        return {
          success: false,
          paymentCompleted: false,
          creditsPurchased: 0,
          error: sessionError.message
        };
      }

      const isPaid = sessionData.payment_completed;
      const isAdCreativePurchase = sessionData.is_credit_purchase && 
                                  sessionData.user_id === user.id &&
                                  sessionData.package_name &&
                                  sessionData.credits_purchased > 0;

      if (!isPaid || !isAdCreativePurchase) {
        return {
          success: true,
          paymentCompleted: false,
          creditsPurchased: 0
        };
      }

      // For ad creative credits, we rely ONLY on webhook processing
      // Check if webhook has processed this transaction
      const { data: existingTransaction, error: transactionError } = await supabase
        .from('credit_transactions')
        .select('id')
        .eq('user_id', user.id)
        .eq('credit_type', 'ad_creative')
        .eq('transaction_type', 'purchase')
        .or(`related_payment_intent_id.eq.${sessionData.payment_intent_id || ''},metadata->>stripeSessionId.eq.${sessionId},metadata->>stripePaymentIntentId.eq.${sessionData.payment_intent_id || ''}`)
        .maybeSingle();

      if (transactionError && transactionError.code !== 'PGRST116') {
        console.error('Error checking existing transaction:', transactionError);
      }

      // If credits processed by webhook, return success
      if (existingTransaction) {
        console.log('✅ Ad creative credits processed by webhook');
        return {
          success: true,
          paymentCompleted: true,
          creditsPurchased: sessionData.credits_purchased
        };
      }

      // If webhook hasn't processed yet, return success but indicate credits not yet processed
      // The webhook will handle the credit addition automatically
      console.log('⏳ Payment completed, waiting for webhook to process ad creative credits');
      return {
        success: true,
        paymentCompleted: true,
        creditsPurchased: sessionData.credits_purchased
      };
    } catch (error) {
      console.error('Error verifying purchase:', error);
      return {
        success: false,
        paymentCompleted: false,
        creditsPurchased: 0,
        error: 'Failed to verify purchase status'
      };
    }
  }
}

export default AdCreativeCreditService;